// GENERATED by `pnpm run gen:narrative` from src/core/content/narrative/intro.md — DO NOT EDIT.
// Hand edits here are drift by definition: edit the markdown source and regenerate
// (the gen-narrative verify gate rebuilds this file and byte-compares it).
// Data only — the types + helpers stay hand-written in intro.ts, which re-exports this.

import { COLD_OPEN } from './coldOpen';
import { getDialogueLine } from './dialogue';
import { NAMES } from './names';
import { NPC_NAME } from './voices';
import type { DialogueScene } from './intro';

export const DIALOGUE_SCENES: readonly DialogueScene[] = [
  {
    id: 'dream',
    title: 'what the water takes',
    voice: 'narrator',
    greeting: [{ voice: 'narrator', text: COLD_OPEN.dream }],
    topics: [],
    decision: {
      prompt: 'One can be kept before the rest goes down. Which?',
      options: [
        {
          id: 'dream-dwell',
          label: 'Hold the knot',
          say: '"The knot. Tied twice. Why twice."',
          react:
            'He turns it for as long as the water allows. The knot goes down with the rest; the looking stays — after this, anything put in his hands gets looked at twice before it gets an answer.',
          stat: { up: 'int', down: 'spd' },
          perk: {
            name: 'The Inward Turn',
            desc: 'A mind that deepens by dwelling, at the price of a slower body.',
          },
        },
        {
          id: 'dream-shake',
          label: 'Strike the list',
          say: '"Enough counting. Up is that way."',
          react:
            'The items go under and the water comes plain: one direction, lighter than the rest. What he keeps is the habit of it — any room he enters after this, the way out is the first thing counted.',
          stat: { up: 'spd', down: 'int' },
          perk: {
            name: 'The Clear Room',
            desc: 'Senses sharpened to the way out — quick where thought is thin.',
          },
        },
        {
          id: 'dream-hands',
          label: 'Take up the load',
          say: '"The load was mine. I know the weight."',
          react:
            "The shoulders answer before the count does. The head kept nothing, but the hands kept the shape of the weight — a strap's width, the length of a carried step.",
          stat: { up: 'str', down: 'luck' },
          perk: {
            name: "The Porter's Hands",
            desc: 'Hands that remember the work before the head does.',
          },
        },
      ],
    },
  },

  {
    id: 'soan',
    title: 'no name to give',
    voice: 'physician',
    speaker: 'soan',
    greeting: [
      { voice: 'narrator', text: COLD_OPEN.weir },
      { voice: 'narrator', text: COLD_OPEN.wake },
      {
        voice: 'narrator',
        text: 'The physician does not ask how you feel. He takes your wrist, then your jaw, then turns your hands over and looks at them longer than at anything else. He presses a thumb into the callus line across one palm and says nothing. There is a ledger open on his knee.',
      },
      { voice: 'physician', speaker: NPC_NAME.soan, text: '"Do you know the year?"' },
      { voice: 'player', text: '"No."' },
      {
        voice: 'physician',
        speaker: NPC_NAME.soan,
        text: '"An\'ei nine. Now you know it again. Drink this."',
      },
      {
        voice: 'narrator',
        text: 'Whatever the hands told him goes into the ledger, two lines, too far off to read. The door slides, and he closes the book on his thumb.',
      },
      {
        voice: 'narrator',
        text: 'The man in the doorway is grey and dry and carries a book of his own. Two books in the room now, and only one of them open.',
      },
      { voice: 'steward', speaker: NPC_NAME.genemon, text: '"The weir man. Sōan — can he work?"' },
      { voice: 'physician', speaker: NPC_NAME.soan, text: '"Ask him."' },
      {
        voice: 'steward',
        speaker: NPC_NAME.genemon,
        text: '"Name, age, where from. In that order."',
      },
      {
        voice: 'narrator',
        text: 'Three answers, and you do not have one of them. The silence goes on long enough that the physician looks up.',
      },
      { voice: 'player', text: '"What name did I give? When they pulled me out."' },
      {
        voice: 'physician',
        speaker: NPC_NAME.soan,
        text: '"None. You gave none. Three days I have had nothing to call you, and neither have you."',
      },
      { voice: 'narrator', text: 'Nothing comes.' },
      {
        voice: 'narrator',
        text: 'Genemon does not look surprised. He looks down and writes, and reads it back as he writes, aloud and once.',
      },
      { voice: 'steward', speaker: NPC_NAME.genemon, text: COLD_OPEN.daybook },
      {
        voice: 'steward',
        speaker: NPC_NAME.genemon,
        text: '"When he can stand, he rakes. The kitchen wants water before it wants anything."',
      },
      {
        voice: 'narrator',
        text: 'He goes. Sōan opens his ledger again, where his thumb kept the place.',
      },
    ],
    topics: [
      {
        id: 'soan-where',
        label: '"Where is this?"',
        answer: [
          {
            voice: 'physician',
            speaker: NPC_NAME.soan,
            text: `"The ${NAMES.house} house — the estate, or what answers to the word. This is my sickroom, off the outer court, and you will be out of it by tomorrow. I need the mat."`,
          },
        ],
      },
      {
        id: 'soan-kami',
        label: '"Someone on the bank said a god hid me."',
        answer: [
          {
            voice: 'physician',
            speaker: NPC_NAME.soan,
            text: '"Kamikakushi. The valley says it of every man the river takes and every child who runs off. Let them say it; it comforts them and costs you nothing. What took you was water."',
          },
        ],
      },
      {
        id: 'soan-how-long',
        label: '"How long was I in the river?"',
        answer: [
          {
            voice: 'physician',
            speaker: NPC_NAME.soan,
            text: '"That I can\'t write down. Three days here, fevered. Before that, ask the river."',
          },
        ],
      },
    ],
    decision: {
      prompt: 'No name, then. What do you keep instead?',
      options: [
        {
          id: 'soan-knot',
          label: '"My hands know a knot. I\'ll keep that."',
          say: '"My hands know a knot. I\'ll keep that."',
          react:
            "You tie it in the cord of your own bedding: a carrier's hitch, fast and one-handed, twice. Sōan watches it both times. He does not write it down while you are looking.",
          stat: { up: 'agi', down: 'int' },
          perk: { name: "The Carrier's Hitch", desc: 'fast, one-handed, holds wet.' },
          memory: { npc: 'soan', regard: 'noted', warmth: 1 },
        },
        {
          id: 'soan-work',
          label: '"The work. Point me at it."',
          say: '"The work. Point me at it."',
          react:
            '"Can you stand? Then the rake is by the door, and the well is past the kitchen. Water first — the kitchen never has enough. Come back when something bleeds."',
          stat: { up: 'str', down: 'luck' },
          perk: { name: 'The Well Yoke', desc: 'a load the shoulders carry before it is asked.' },
          memory: { npc: 'soan', regard: 'plain', warmth: 0 },
        },
        {
          id: 'soan-count',
          label: '"The days. I\'ll count from this one."',
          say: '"The days. I\'ll count from this one."',
          react:
            '"Day four, by mine. Yours can start where you like. Mark the post — notches hold."',
          stat: { up: 'int', down: 'agi' },
          perk: { name: 'The Notched Post', desc: 'a day cut where fever cannot blur it.' },
          memory: { npc: 'soan', regard: 'methodical', warmth: 0 },
        },
      ],
    },
  },

  {
    id: 'genemon',
    title: 'entered in the book',
    voice: 'steward',
    speaker: 'genemon',
    greeting: [
      {
        voice: 'steward',
        speaker: NPC_NAME.genemon,
        text: getDialogueLine('genemon-open', 'gen-greet').text,
      },
    ],
    topics: [
      {
        id: 'gen-house',
        label: '"What house is this?"',
        answer: [
          {
            voice: 'steward',
            speaker: NPC_NAME.genemon,
            text: `"The ${NAMES.house}. On the lord's rolls: samurai, one house, standing since before my hand kept the book. In the granary: less than the rolls would suggest, and I will not write the number for a stranger."`,
          },
        ],
      },
      {
        id: 'gen-work',
        label: '"What\'s the work?"',
        answer: [
          {
            voice: 'steward',
            speaker: NPC_NAME.genemon,
            text: '"Rice. Half a season\'s stores, spilled in the kura court where the door gave way in the rains. You rake it in before the rain spoils what\'s left to count; wage stands as entered — meals."',
          },
        ],
      },
      {
        id: 'gen-you',
        label: '"Who are you to me?"',
        answer: [
          {
            voice: 'steward',
            speaker: NPC_NAME.genemon,
            text: '"The steward. I count what the house holds and what it is owed; you came in under the second column. A man working a thing off gets fair terms from me, and no questions the book does not ask."',
          },
        ],
      },
      {
        id: 'gen-danger',
        label: '"Is the work safe?"',
        gate: (asked) => asked.has('gen-work'),
        answer: [
          {
            voice: 'steward',
            speaker: NPC_NAME.genemon,
            text: '"A wolf, one, gone bold at the grain store. Grain lost: some. Men lost: none — work by daylight and I intend that count to hold."',
          },
        ],
      },
    ],
    decision: {
      prompt: 'The brush waits over the condition column. How do you answer the steward?',
      options: [
        {
          id: 'genemon-earnest',
          label: 'Point me at the work',
          say: '"Give me the rake. I\'ll do whatever\'s put in front of me."',
          react:
            '"Condition: sound; willing. I enter you as my charge — mine to set to the task, mine to answer for."',
          stat: { up: 'str', down: 'agi' },
          perk: {
            name: `${NAMES.elder}'s Charge`,
            desc: 'Honest muscle set plainly to the task — sure over nimble.',
          },
          memory: { npc: 'genemon', regard: 'earnest', warmth: 1 },
        },
        {
          id: 'genemon-wary',
          label: "First, what's in it for me",
          say: '"Before I rake anything — what do I get, and what do you get?"',
          react:
            '"Terms asked before work; I have seen worse habits in honest men. Condition: guarded — a man who counts before he lifts keeps his feet under him."',
          stat: { up: 'agi', down: 'str' },
          perk: {
            name: 'The Wary Foot',
            desc: 'A guard kept up and light on the feet — quick to move before committing.',
          },
          memory: { npc: 'genemon', regard: 'wary', warmth: -1 },
        },
        {
          id: 'genemon-steady',
          label: 'Just get to work',
          say: '(You say nothing, and take up the rake.)',
          react:
            '"No answer; the rake already moving. Condition: answers with hands — I enter it so, and luck has no column in this book."',
          stat: { up: 'spd', down: 'luck' },
          perk: {
            name: 'Hands Before Words',
            desc: 'A steady quickness that answers with work — trusting to no luck.',
          },
          memory: { npc: 'genemon', regard: 'steady', warmth: 1 },
        },
      ],
    },
  },
];
